import { ApiEndpoint, Config, ICmTableFieldDef, MapLayerConfig } from "../types";

// -----------------------------------------------------------------------------
// TEMPORARY ANTI-CORRUPTION BRIDGE
// -----------------------------------------------------------------------------
// This module is a *temporary* boundary that normalises the `GET /api/v2/config/`
// response into the legacy `Config` shape the app already consumes. The agreed
// target (see the config-migration PR review) is for the backend to converge on
// the legacy `Config` shape directly, at which point this adapter collapses to a
// pass-through and can be deleted. Until then it is the single reconciliation
// point for remote config, and the boundary below is concretely typed so a
// backend rename fails at compile time instead of silently yielding `undefined`.
// -----------------------------------------------------------------------------

/** Config schema major version this client knows how to read. */
export const SUPPORTED_CONFIG_MAJOR = 1;

/** A nested `apiEndpoints[]` entry from `GET /api/v2/config/` (snake_case). */
export interface RemoteApiEndpoint {
  slug: string;
  name: string;
  description?: string;
  spatial_type?: ApiEndpoint["spatialType"];
  field_schema?: ICmTableFieldDef[];
  map_display_config?: { icon?: string; color?: string; style?: Record<string, any> };
  extras?: Record<string, any>;
  api_path?: string | null;
  geojson_path?: string | null;
}

/** A Fulcrum entry from `externalDataSources[]`. */
export interface RemoteExternalDataSource {
  name: string;
  formId: string;
  tableName: string;
  description?: string;
  dataSourceType?: string;
  dataSourceConfig?: ApiEndpoint["dataSourceConfig"];
  dateConfig?: ApiEndpoint["dateConfig"];
}

/** A `homePageShortcuts[]` entry (snake_case). */
export interface RemoteHomePageShortcut {
  name: string;
  target_tab: number;
  table_slug?: string;
}

/** UI flags served under the `uiConfig` catch-all wrapper. */
export interface RemoteUiConfig {
  sidebarOpen?: boolean;
  primaryFilterLayer?: string;
  defaultDataTable?: string;
  showMediaInRelatedItemsCloud?: boolean;
  showGeoInRelatedItemsCloud?: boolean;
  alwaysRequestRelatedItemsAndMedia?: boolean;
  mediaUpload?: Config["mediaUpload"];
  enableZoomToRelatedItems?: boolean;
  hideCardsWithoutImages?: boolean;
  limitDataDrawerToExtent?: boolean;
  enableServerSearch?: boolean;
  [key: string]: any;
}

/**
 * Concrete response shape of `GET /api/v2/config/` (database-driven config).
 *
 * Top-level keys are camelCase (`appTitle`, `authConfig`, `uiConfig`, ...), while the
 * nested `apiEndpoints[]` entries use snake_case (`slug`, `api_path`, `spatial_type`,
 * `field_schema`). This adapter normalises everything back into the legacy {@link Config}
 * shape that the existing components/hooks still consume, so no consumer changes are
 * required during the migration.
 */
export interface RemoteConfigResponse {
  schema_version: string;
  appTitle: string;
  logoPath: string;
  loginImagePath: string;
  loginMessage: string;
  colorScheme: Config["colorScheme"];
  homePageTitle: string;
  homePageDescription: string;
  homePageShortcuts?: RemoteHomePageShortcut[];
  initialMapView: Config["initialMapView"];
  mapLayers?: Array<MapLayerConfig & { source?: string }>;
  apiEndpoints?: RemoteApiEndpoint[];
  dashboardUrls?: Config["dashboardUrls"];
  authConfig?: Config["auth"];
  uiConfig?: RemoteUiConfig;
  externalDataSources?: RemoteExternalDataSource[];
  tabularData?: Config["tabularData"];
}

/** Extracts the major version from a `schema_version` string (e.g. "1.0" -> 1). */
const parseSchemaMajor = (version: unknown): number | null => {
  if (typeof version !== "string") return null;
  const major = parseInt(version.split(".")[0], 10);
  return Number.isNaN(major) ? null : major;
};

/**
 * Detects the backend config by its `schema_version` marker AND branches on the
 * major version: an unknown/newer major is rejected (returns false) so the caller
 * falls back to a known-good config instead of mis-parsing a shape it doesn't
 * understand.
 */
export const isRemoteConfig = (raw: any): raw is RemoteConfigResponse => {
  if (!raw || typeof raw !== "object" || typeof raw.schema_version !== "string") {
    return false;
  }
  const major = parseSchemaMajor(raw.schema_version);
  if (major === null) {
    console.warn(`[configAdapter] Unrecognised schema_version "${raw.schema_version}" — ignoring remote config.`);
    return false;
  }
  if (major !== SUPPORTED_CONFIG_MAJOR) {
    console.warn(
      `[configAdapter] Unsupported config schema_version "${raw.schema_version}" ` +
        `(this client supports major ${SUPPORTED_CONFIG_MAJOR}). Ignoring remote config to avoid mis-parsing.`
    );
    return false;
  }
  return true;
};

/** Legacy `api_endpoint` values are relative (`api/v2/...`) with no leading slash. */
const stripLeadingSlash = (value?: string | null): string => (value ? value.replace(/^\/+/, "") : "");

/** Unique tableName for the synthesized "Tabular data" tab (dataset_type='tabular'). */
export const TABULAR_DATASETS_TABLE = "spatial-datasets-tabular";

/**
 * System endpoint the backend does not (yet) include in the GET response but which
 * dozens of components look up by the hardcoded `tableName` "restrictions".
 */
const RESTRICTIONS_ENDPOINT: ApiEndpoint = {
  name: "Restrictions",
  tableName: "restrictions",
  relationshipKey: "restrictions",
  queryParam: "restrictions",
  api_endpoint: "api/v2/restrictions/",
  description: "Restrictions is a collection of all the access control groups in the database"
};

const inferEntityType = (slug: string, spatialType?: string): ApiEndpoint["entityType"] => {
  if (slug === "media") return "media";
  if (slug === "spatialdataset" || slug === "spatial-datasets") return "spatial-dataset";
  return spatialType && spatialType !== "none" ? "geo" : "table";
};

/** Maps a raw `apiEndpoints[]` entry (CM table or system) to the legacy {@link ApiEndpoint}. */
const adaptEndpoint = (ep: RemoteApiEndpoint): ApiEndpoint => {
  const slug: string = ep.slug;
  const spatialType = ep.spatial_type;
  const isMedia = slug === "media";
  const isSpatialDataset = slug === "spatialdataset" || slug === "spatial-datasets";
  const isSystem = isMedia || isSpatialDataset;

  // System endpoints keep their legacy tableName; CM tables use the slug verbatim.
  const tableName = isSpatialDataset ? "spatial-datasets" : slug;

  const endpoint: ApiEndpoint = {
    name: ep.name,
    tableName,
    relationshipKey: tableName,
    tableSlug: slug,
    api_endpoint: stripLeadingSlash(ep.api_path),
    description: ep.description || "",
    entityType: inferEntityType(slug, spatialType),
    spatialType,
    fields: ep.field_schema || []
  };

  if (!isSystem) {
    // CM-table map layers are generated at runtime by useCmTableMapLayers with the
    // id `cm-table-{slug}`; link the endpoint to that layer id.
    endpoint.dataSourceType = "cm-table";
    endpoint.mapLayerId = `cm-table-${slug}`;
    if (ep.map_display_config?.style) {
      endpoint.style = ep.map_display_config.style;
    }
  }

  if (isMedia) {
    endpoint.queryParam = "media";
    endpoint.mapLayerId = "Geotagged Photos";
    endpoint.sortBy = "id";
    endpoint.sortDirection = "desc";
  } else if (isSpatialDataset) {
    endpoint.sortBy = "id";
    endpoint.sortDirection = "desc";
  }

  return endpoint;
};

/** Converts an `externalDataSources[]` (Fulcrum) entry into a legacy {@link ApiEndpoint}. */
const adaptFulcrumSource = (src: RemoteExternalDataSource): ApiEndpoint => ({
  name: src.name,
  tableName: src.tableName,
  relationshipKey: src.tableName,
  formId: src.formId,
  dataSourceType: "fulcrum",
  // Fulcrum map layers use the source name as their layer id.
  mapLayerId: src.name,
  entityType: "geo",
  api_endpoint: "",
  description: src.description || "",
  dataSourceConfig: src.dataSourceConfig,
  dateConfig: src.dateConfig
});

const adaptHomePageShortcuts = (shortcuts: RemoteHomePageShortcut[] = []): Config["homePageShortcuts"] =>
  shortcuts.map((s) => ({
    name: s.name,
    targetTab: s.target_tab,
    options: { tableName: s.table_slug || "" }
  }));

/**
 * Normalises a `GET /api/v2/config/` response into the legacy {@link Config} shape.
 */
export const adaptRemoteConfig = (raw: RemoteConfigResponse): Config => {
  const ui = raw.uiConfig || {};

  const cmEndpoints: ApiEndpoint[] = (raw.apiEndpoints || []).map(adaptEndpoint);
  const fulcrumEndpoints: ApiEndpoint[] = (raw.externalDataSources || []).map(adaptFulcrumSource);
  const apiEndpoints = [...cmEndpoints, ...fulcrumEndpoints];

  if (!apiEndpoints.some((e) => e.tableName === "restrictions")) {
    apiEndpoints.push(RESTRICTIONS_ENDPOINT);
  }

  // Tabular (CSV → Parquet / Power BI) datasets are SpatialDatasets with
  // dataset_type='tabular'. The backend excludes them from the default
  // /spatial-datasets/ list, so they get their own tab that queries
  // ?dataset_type=tabular (from tabularData.listEndpoint). We reuse the
  // spatial-dataset entityType so the existing list/detail machinery applies;
  // tabular-specific UI is gated on dataset_type inside SpatialDatasetDetail.
  const tabular = raw.tabularData;
  if (tabular?.listEndpoint && !apiEndpoints.some((e) => e.tableName === TABULAR_DATASETS_TABLE)) {
    apiEndpoints.push({
      name: tabular.name || "Tabular data",
      tableName: TABULAR_DATASETS_TABLE,
      relationshipKey: TABULAR_DATASETS_TABLE,
      tableSlug: TABULAR_DATASETS_TABLE,
      api_endpoint: stripLeadingSlash(tabular.listEndpoint),
      description: "Tabular datasets (CSV → Parquet) available for Power BI and download",
      entityType: "spatial-dataset",
      sortBy: "id",
      sortDirection: "desc"
    });
  }

  // CM-table map layers are generated at runtime by useCmTableMapLayers from the
  // apiEndpoints (id: `cm-table-{slug}`). Drop the backend's `source: "cmtable"`
  // entries so we don't render two layers per table.
  const mapLayers = (raw.mapLayers || []).filter((layer) => layer.source !== "cmtable") as MapLayerConfig[];

  return {
    schema_version: raw.schema_version,
    appTitle: raw.appTitle,
    logoPath: raw.logoPath,
    loginImagePath: raw.loginImagePath,
    loginMessage: raw.loginMessage,
    colorScheme: raw.colorScheme,
    homePageTitle: raw.homePageTitle,
    homePageDescription: raw.homePageDescription,
    homePageShortcuts: adaptHomePageShortcuts(raw.homePageShortcuts),
    initialMapView: raw.initialMapView,
    mapLayers,
    apiEndpoints,
    dashboardUrls: raw.dashboardUrls,
    auth: raw.authConfig,

    // Lift uiConfig flags to the root level where legacy consumers read them.
    sidebarOpen: ui.sidebarOpen ?? false,
    primaryFilterLayer: ui.primaryFilterLayer ?? "",
    defaultDataTable: ui.defaultDataTable,
    showMediaInRelatedItemsCloud: ui.showMediaInRelatedItemsCloud,
    showGeoInRelatedItemsCloud: ui.showGeoInRelatedItemsCloud,
    alwaysRequestRelatedItemsAndMedia: ui.alwaysRequestRelatedItemsAndMedia,
    mediaUpload: ui.mediaUpload,
    enableZoomToRelatedItems: ui.enableZoomToRelatedItems,
    hideCardsWithoutImages: ui.hideCardsWithoutImages,
    limitDataDrawerToExtent: ui.limitDataDrawerToExtent,
    enableServerSearch: ui.enableServerSearch,
    tabularData: raw.tabularData
  };
};
